import React from 'react'
import styled from '@emotion/styled'
import { Divider, Stack, Typography } from '@mui/material'
import { Inputs } from '../../../containers/facilityReservationTodayConditionInput/facilityReservationTodayConditionInputService'
import { Control } from 'react-hook-form'
import { GetTodayConditionInputDetailDto } from '../../../dataAccess/webApi/dto/reservationsDto'
import { GContainer, GItem } from '../common/grids'
import { TitleLabel } from '../common/titleLabel'
import { AttentionLabel } from '../common/attentionLabel'
import { RadioButtonGroup } from '../common/inputs/radioButtonGroup'
import { TimePicker } from '../common/inputs/timePicker'
import { Select } from '../common/inputs/select'
import { PostDecodeMstDto } from '../../../dataAccess/webApi/dto/decodeMstDto'
import {
  todayConditionInputFieldUsage,
  todayConditionInputNapType,
} from '../../../containers/common/constant/classification'
import { translate } from '../../../i18n'

const SectionText = styled(Typography)(() => ({
  fontSize: '21px',
  marginTop: '10px',
}))

const BlackDivider = styled(Divider)(() => ({
  borderColor: '#000',
  marginBottom: '20px',
}))

type SleepSecProps = {
  control: Control<Inputs>
  todayConditionInputFieldsInfo: GetTodayConditionInputDetailDto['todayConditionInputFieldsInfo']
  napTypeDecodeMst: PostDecodeMstDto[]
  isNap: boolean
  napType: string | null
  yesNos: {
    value: string
    label: string
  }[]
}

// 睡眠セクション
const SleepSec: React.FC<SleepSecProps> = (props) => {
  const { control, todayConditionInputFieldsInfo, napTypeDecodeMst, isNap, napType, yesNos } = props

  // お昼寝の種類用の選択肢オプションを編集
  const napTypeValueLabels = napTypeDecodeMst.map((item) => {
    return {
      value: item.code,
      label: item.name,
    }
  })

  return (
    <GItem xs={12}>
      {/* セクションタイトル部分 */}
      <GContainer>
        <GItem xs={12}>
          <SectionText>{translate('facilityReservationTodayConditionInput.section.sleep')}</SectionText>
          <BlackDivider />
        </GItem>
      </GContainer>
      {/* フォーム部分 */}
      <Stack spacing={2}>
        {todayConditionInputFieldsInfo &&
          todayConditionInputFieldsInfo.lastNightSleep !== todayConditionInputFieldUsage.noUse && (
            <>
              {/* 昨夜はよく眠りましたか */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.lastNightSleep')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <RadioButtonGroup
                      name={'lastNightSleep'}
                      label={translate('facilityReservationTodayConditionInput.form.label.lastNightSleep')}
                      row
                      required={
                        todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary
                      }
                      buttonValueLabels={yesNos}
                      control={control}
                    />
                  </GItem>
                </GContainer>
              </GItem>

              {/* 昨夜の就寝時刻 */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.lastNightFromTime')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                      <TimePicker
                        name={'lastNightFromTime'}
                        required={
                          todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary
                        }
                        control={control}
                      />
                    </GItem>
                  </GItem>
                </GContainer>
              </GItem>

              {/* 今朝の起床時刻 */}
              <GItem>
                <TitleLabel>
                  {todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {translate('facilityReservationTodayConditionInput.form.label.lastNightToTime')}
                </TitleLabel>
                <GContainer alignItems="flex-end">
                  <GItem xs={12}>
                    <GItem sx={{ width: { xs: '100px', sm: '160px' }}}>
                      <TimePicker
                        name={'lastNightToTime'}
                        required={
                          todayConditionInputFieldsInfo.lastNightSleep === todayConditionInputFieldUsage.necessary
                        }
                        control={control}
                      />
                    </GItem>
                  </GItem>
                </GContainer>
              </GItem>
            </>
          )}

        {todayConditionInputFieldsInfo && todayConditionInputFieldsInfo.nap !== todayConditionInputFieldUsage.noUse && (
          <>
            {/* 普段お昼寝をしますか */}
            <GItem>
              <TitleLabel>
                {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary && (
                  <AttentionLabel>*</AttentionLabel>
                )}
                {translate('facilityReservationTodayConditionInput.form.label.nap')}
              </TitleLabel>
              <GContainer alignItems="flex-end">
                <GItem xs={12}>
                  <RadioButtonGroup
                    name={'nap'}
                    label={translate('facilityReservationTodayConditionInput.form.label.nap')}
                    row
                    required={todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary}
                    buttonValueLabels={yesNos}
                    control={control}
                  />
                </GItem>
              </GContainer>
            </GItem>

            {isNap && (
              <>
                {/* お昼寝の種類 */}
                <GItem>
                  <TitleLabel>
                    {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary && isNap && (
                      <AttentionLabel>*</AttentionLabel>
                    )}
                    {translate('facilityReservationTodayConditionInput.form.label.napType')}
                  </TitleLabel>
                  <GContainer alignItems="flex-end">
                    <GItem xs={12}>
                      <Select
                        fullWidth
                        name={'napType'}
                        label={translate('facilityReservationTodayConditionInput.form.label.napType')}
                        valueLabels={napTypeValueLabels}
                        required={
                          todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary && isNap
                        }
                        control={control}
                      />
                    </GItem>
                  </GContainer>
                </GItem>
              </>
            )}

            {isNap &&
              (napType === todayConditionInputNapType.amOnly || napType === todayConditionInputNapType.both) && (
                <>
                  {/* 午前睡眠開始時刻 */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                        isNap &&
                        (napType === todayConditionInputNapType.amOnly ||
                          napType === todayConditionInputNapType.both) && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.napAmFromTime')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TimePicker
                          name={'napAmFromTime'}
                          required={
                            todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                            isNap &&
                            (napType === todayConditionInputNapType.amOnly ||
                              napType === todayConditionInputNapType.both)
                          }
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                  {/* 午前睡眠終了時刻 */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                        isNap &&
                        (napType === todayConditionInputNapType.amOnly ||
                          napType === todayConditionInputNapType.both) && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.napAmToTime')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TimePicker
                          name={'napAmToTime'}
                          required={
                            todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                            isNap &&
                            (napType === todayConditionInputNapType.amOnly ||
                              napType === todayConditionInputNapType.both)
                          }
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}

            {isNap &&
              (napType === todayConditionInputNapType.pmOnly || napType === todayConditionInputNapType.both) && (
                <>
                  {/* 午後睡眠開始時刻 */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                        isNap &&
                        (napType === todayConditionInputNapType.pmOnly ||
                          napType === todayConditionInputNapType.both) && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.napPmFromTime')}
                    </TitleLabel>
                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TimePicker
                          name={'napPmFromTime'}
                          required={
                            todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                            isNap &&
                            (napType === todayConditionInputNapType.pmOnly ||
                              napType === todayConditionInputNapType.both)
                          }
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>

                  {/* 午後睡眠終了時刻 */}
                  <GItem>
                    <TitleLabel>
                      {todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                        isNap &&
                        (napType === todayConditionInputNapType.pmOnly ||
                          napType === todayConditionInputNapType.both) && <AttentionLabel>*</AttentionLabel>}
                      {translate('facilityReservationTodayConditionInput.form.label.napPmToTime')}
                    </TitleLabel>

                    <GContainer alignItems="flex-end">
                      <GItem xs={12}>
                        <TimePicker
                          name={'napPmToTime'}
                          required={
                            todayConditionInputFieldsInfo.nap === todayConditionInputFieldUsage.necessary &&
                            isNap &&
                            (napType === todayConditionInputNapType.pmOnly ||
                              napType === todayConditionInputNapType.both)
                          }
                          control={control}
                        />
                      </GItem>
                    </GContainer>
                  </GItem>
                </>
              )}
          </>
        )}
      </Stack>
    </GItem>
  )
}

export default SleepSec
